import { useState, useEffect, useCallback } from 'react';

/**
 * usePwaInstallPrompt — tracks Progressive Web App install state across iOS
 * Safari and Android Chromium-based browsers, with a dismiss cooldown.
 *
 * Returns:
 *   {
 *     canPrompt    — true if the browser is in a state where we should show
 *                    SOMETHING ("Install" button OR iOS instructions).
 *     platform     — 'ios' | 'android' | 'other'
 *     isInstalled  — true if the app is already running in standalone mode
 *                    (PWA on homescreen) or iOS Safari's standalone variant.
 *     promptInstall — async fn that triggers native install on Android, or
 *                     resolves to `false` on iOS (caller shows instructions).
 *     dismiss      — persist a dismissal that hides the prompt for 14 days.
 *   }
 *
 * Listens for `beforeinstallprompt` (captured globally so late mounts still
 * pick it up), and `appinstalled` (hides the prompt after success).
 */

const DISMISS_KEY = 'flowcore.pwaInstall.dismissedAt';
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000;

function detectPlatform() {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  const isIPad =
    /iPad/.test(ua)
    || (navigator.maxTouchPoints > 1 && /Macintosh/.test(ua)); // iPadOS 13+ masquerades as Mac
  if (/iPhone|iPod/.test(ua) || isIPad) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'other';
}

function isStandaloneDisplay() {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  if (typeof navigator !== 'undefined' && navigator.standalone === true) return true;
  return false;
}

function isRecentlyDismissed() {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_COOLDOWN_MS;
  } catch (_) {
    return false;
  }
}

// Global capture of the beforeinstallprompt event so that even if the
// component mounts after the event has fired, we can still offer install.
if (typeof window !== 'undefined' && !window.__flowcoreDeferredInstall) {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    window.__flowcoreDeferredInstall = e;
    try { window.dispatchEvent(new CustomEvent('flowcore-pwa-installable')); } catch (_) {}
  });
  window.addEventListener('appinstalled', () => {
    window.__flowcoreDeferredInstall = null;
    try { window.dispatchEvent(new CustomEvent('flowcore-pwa-installed')); } catch (_) {}
  });
}

export function usePwaInstallPrompt() {
  const [platform] = useState(detectPlatform);
  const [isInstalled, setIsInstalled] = useState(isStandaloneDisplay);
  const [deferredEvent, setDeferredEvent] = useState(
    () => (typeof window !== 'undefined' && window.__flowcoreDeferredInstall) || null
  );
  const [dismissed, setDismissed] = useState(isRecentlyDismissed);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const onInstallable = () => setDeferredEvent(window.__flowcoreDeferredInstall || null);
    const onInstalled = () => {
      setDeferredEvent(null);
      setIsInstalled(true);
    };
    const mql = window.matchMedia && window.matchMedia('(display-mode: standalone)');
    const onDisplayChange = () => setIsInstalled(isStandaloneDisplay());

    window.addEventListener('flowcore-pwa-installable', onInstallable);
    window.addEventListener('flowcore-pwa-installed', onInstalled);
    if (mql && mql.addEventListener) mql.addEventListener('change', onDisplayChange);

    return () => {
      window.removeEventListener('flowcore-pwa-installable', onInstallable);
      window.removeEventListener('flowcore-pwa-installed', onInstalled);
      if (mql && mql.removeEventListener) mql.removeEventListener('change', onDisplayChange);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredEvent) return false;
    try {
      deferredEvent.prompt();
      const choice = await deferredEvent.userChoice;
      if (choice && choice.outcome === 'accepted') {
        setDeferredEvent(null);
        return true;
      }
    } catch (_) { /* ignore */ }
    return false;
  }, [deferredEvent]);

  const dismiss = useCallback(() => {
    try { window.localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) {}
    setDismissed(true);
  }, []);

  // iOS: always "promptable" on mobile Safari until dismissed or installed.
  // Android: only when the browser has fired beforeinstallprompt.
  const canPrompt =
    !isInstalled
    && !dismissed
    && (
      platform === 'ios'
        ? true
        : !!deferredEvent
    );

  return {
    canPrompt,
    platform,
    isInstalled,
    isNative: platform === 'ios' || platform === 'android',
    promptInstall,
    dismiss,
  };
}
